/* global globalThis */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Auth from "./router/auth";
import Layout from "./router/layout";
import VideoBluegecko from "./component/pages/landingpage/Video";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { LoaderCircle } from "lucide-react";

// Landing route: normally shows the marketing video page, but if the user
// just signed in via the "Admin Settings" entry, send them to the admin page.
const LandingRoute = ({ isAuthenticated }) => {
  if (isAuthenticated && sessionStorage.getItem("postLoginAdmin")) {
    sessionStorage.removeItem("postLoginAdmin");
    return <Navigate to="/organization-management" replace />;
  }
  return <VideoBluegecko />;
};

LandingRoute.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
};

const App = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Wait for MSAL to finish initializing and handle silent SSO
  useEffect(() => {
    const performSSOSilent = async () => {
      if (isAuthenticated) {
        setIsInitializing(false);
        return;
      }

      // Check for incoming login_hint query parameter
      const urlParams = new URLSearchParams(globalThis.location.search);
      const loginHint = urlParams.get("login_hint");

      if (loginHint) {
        try {
          console.log("[SSO] Detected login_hint in URL, attempting silent sign-in...");
          const silentResult = await instance.ssoSilent({
            scopes: ["openid", "profile"],
            loginHint: loginHint,
          });

          if (silentResult) {
            console.log("[SSO] Silent sign-in succeeded!");
            return;
          }
        } catch (error) {
          console.warn(
            "[SSO] Silent sign-in failed, attempting silent login redirect fallback...",
            error,
          );
          try {
            // Auto-redirect since session cookie exists but interaction is required (e.g. MFA, consent)
            await instance.loginRedirect({
              scopes: ["openid", "profile"],
              loginHint: loginHint,
            });
            return;
          } catch (redirectError) {
            console.error("[SSO] Auto-redirect login failed:", redirectError);
          }
        } finally {
          const params = new URLSearchParams(globalThis.location.search);
          if (params.has("login_hint")) {
            params.delete("login_hint");
            const newSearch = params.toString();
            const cleanPath =
              globalThis.location.pathname + (newSearch ? `?${newSearch}` : "");
            globalThis.history.replaceState({}, document.title, cleanPath);
          }
        }
      }

      // Small delay to ensure state is fully settled
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 100);
      return () => clearTimeout(timer);
    };

    if (inProgress === InteractionStatus.None) {
      performSSOSilent();
    }
  }, [inProgress, isAuthenticated, instance]);

  // Show loading spinner while MSAL is initializing
  if (isInitializing || inProgress !== InteractionStatus.None) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
        <LoaderCircle className="animate-spin text-blue-900" size={50} />
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={1500} style={{ zIndex: 100000 }} />
      <Routes>
        <Route
          path="/"
          element={<LandingRoute isAuthenticated={isAuthenticated} />}
        />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <Layout isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
            ) : (
              <Auth />
            )
          }
        />
      </Routes>
    </Router>
  );
};

export default App;